import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  apiGetProfile,
  apiPostProfile,
  apiPatchProfile,
  apiUploadProfileImage,
  apiDeleteProfileImage,
} from "@api/profile";
import type {
  ProfileCreateParams,
  ProfileCreateRes,
} from "@api/profile";
import type { ProfileRes } from "@/types/profile";
import { dataUrlToBlob } from "@utils/index";

export type { ProfileRes };

export type ProfileNewDataObject = {
  data: Omit<ProfileCreateParams, "page_content">;
  content: ProfileCreateParams["page_content"];
  imageDataUrl?: string | null;
  imageRemoved?: boolean;
};

export function useProfile(id: string | undefined) {
  return useQuery<ProfileRes>({
    queryKey: ["profile", id],
    queryFn: () => apiGetProfile(id!),
    enabled: !!id,
  });
}

export function useCreateProfile() {
  const queryClient = useQueryClient();
  return useMutation<ProfileCreateRes, Error, ProfileNewDataObject>({
    mutationFn: async ({ data, content, imageDataUrl }) => {
      const result = await apiPostProfile({ ...data, page_content: content });
      if (imageDataUrl) {
        const blob = dataUrlToBlob(imageDataUrl);
        await apiUploadProfileImage(result.profile_id, blob);
      }
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profileList"] });
      queryClient.invalidateQueries({ queryKey: ["categories"] });
    },
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
      content,
      imageDataUrl,
      imageRemoved,
    }: ProfileNewDataObject & { id: string }) => {
      const res = await apiPatchProfile({
        params: { ...data, page_content: content },
        id,
      });
      if (imageRemoved) {
        await apiDeleteProfileImage(id);
      } else if (imageDataUrl) {
        const blob = dataUrlToBlob(imageDataUrl);
        await apiUploadProfileImage(id, blob);
      }
      return { res, id, data, content };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["profile", result.id] });
      queryClient.invalidateQueries({ queryKey: ["profileList"] });
      queryClient.invalidateQueries({ queryKey: ["categories"] });
    },
  });
}
